import { useMemo, useState } from 'react';

import {
  Box,
  Button,
  Divider,
  CircularProgress,
  makeStyles,
  MenuItem,
  MenuList,
  Typography,
  useTheme,
} from '@material-ui/core';

import { HiChevronLeft } from 'react-icons/hi';
import { FiMessageCircle } from 'react-icons/fi';
import { LuUsers } from 'react-icons/lu';
import { AiOutlinePlus } from 'react-icons/ai';
import { HiOutlineTrash } from 'react-icons/hi';

import ChatAvatar from './ChatAvatar';
import { getColor, monochrome } from '../../theme/styles';
import { AppIconButton } from '../AppIconButton';
import { ChatData, ChatUser, Image } from '../../types';
import { AppAccordion } from '../AppAccordion';
import { ChatDetailSettingsButton } from './ChatDetailSettingsButton';
import { ChatChangeNameDialog } from './ChatChangeNameDialog';
import { ChatChangeAvatarDialog } from './ChatChangeAvatarDialog';
import { ChatLimitMessageSendersDialog } from './ChatLimitMessageSendersDialog';
import { GridActions } from '../GridActions';
import { ConfirmDialog } from '../ConfirmDialog';

interface Props {
  chat: ChatData;
  onClose: () => void;
  updateChatName: (chatName: string) => Promise<void>;
  updateChatAvatar: (avatar: Image) => Promise<void>;
  loadingChatUpdate: boolean;
  isChatAdmin: boolean;
  updateChatReadOnlyStatus: (readOnly: boolean) => Promise<void>;
  openAddMembersDialog: () => void;
  removeUserFromChat: (userId: string) => Promise<void>;
  goToChat: (chatUser: ChatUser) => Promise<void>;
  userId: string;
}

export const ChatDetails: React.FC<Props> = ({
  chat,
  onClose,
  updateChatName,
  updateChatAvatar,
  loadingChatUpdate,
  isChatAdmin,
  updateChatReadOnlyStatus,
  openAddMembersDialog,
  removeUserFromChat,
  goToChat,
  userId,
}) => {
  const theme = useTheme();
  const colors = getColor(theme.palette.type === 'dark');
  const classes = useStyles({ theme });
  const isAutomatedChat = chat.type === 'company' || chat.type === 'location' || chat.type === 'team';
  const isAbleToChangePhoto = chat.type === 'company' || chat.type === 'team';
  const [openChangeNameDialog, setOpenChangeNameDialog] = useState(false);
  const [openChangeAvatarDialog, setOpenChangeAvatarDialog] = useState(false);
  const [chatName, setChatName] = useState(chat.name || '');
  const [chatAvatar, setChatAvatar] = useState<Image>({ file: null, url: chat.avatarUrl });
  const [readOnly, setReadOnly] = useState(chat.readOnly);
  const [openLimitMessageSendersDialog, setOpenLimitMessageSendersDialog] = useState(false);
  const [openRemoveUserDialog, setOpenRemoveUserDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ChatUser | null>(null);
  const [loadingRemoveUser, setLoadingRemoveUser] = useState(false);
  const [creatingChat, setCreatingChat] = useState(false);

  const changeNameDisabledReason = useMemo(() => {
    if (chat.type === 'company') {
      return 'This chat is automated and cannot be changed.';
    }
    if (chat.type === 'location') {
      return `This chat is linked to the ${chat.name} location. You may change the Location's name in the Locations module.`;
    }
    if (chat.type === 'team') {
      return `This chat is linked to the ${chat.name} team. You may change the Team's name in the Teams module.`;
    }
    return '';
  }, [chat.type, chat.name]);

  const changePhotoDisabledReason = useMemo(() => {
    if (chat.type === 'company') {
      return 'This chat is automated and cannot be changed.';
    }
    if (chat.type === 'team') {
      return `This chat is linked to the ${chat.name} team. You may change the Team's avatar in the Teams module.`;
    }
    return '';
  }, [chat.type, chat.name]);

  const handleRemoveUser = async (userId: string) => {
    setLoadingRemoveUser(true);
    await removeUserFromChat(userId);
    setOpenRemoveUserDialog(false);
    setLoadingRemoveUser(false);
  };

  const handleGoToChat = async (chatUser: ChatUser) => {
    setCreatingChat(true);
    await goToChat(chatUser);
    setCreatingChat(false);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.headerBox}>
        <ChatAvatar
          avatarUrl={chat.avatarUrl}
          size={48}
          chatType={chat.type}
          borderWidth={1}
          borderColor={theme.palette.background.paper}
          online={false}
          name={chat.name || undefined}
        />
        <Box className={classes.textBox}>
          <Typography className={classes.name}>{chat.name}</Typography>
          <Typography className={classes.members}>
            <Box display="flex" alignItems="center" style={{ gap: '4px', marginTop: 6 }}>
              <LuUsers className={classes.membersIcon} />
              {chat.members.length} Group members
            </Box>
          </Typography>
        </Box>

        <AppIconButton
          variant="default"
          disableFocusRipple
          disableRipple
          disableTouchRipple
          onClick={onClose}
        >
          <HiChevronLeft color={theme.palette.text.secondary} />
        </AppIconButton>
      </Box>

      <Divider />

      {isChatAdmin && (
        <AppAccordion
          title="Chat Settings"
          initialExpanded={true}
          titleStyles={{ color: colors.textLight, fontSize: 13 }}
          noPadding
          renderDetailsBlock={() => (
            <Box width="100%" display="flex" marginBottom={2} flexDirection="column" gridGap={16}>
              <ChatDetailSettingsButton
                isAutomatedChat={isAutomatedChat}
                title="Change chat name"
                onClick={() => setOpenChangeNameDialog(true)}
                disabledReason={changeNameDisabledReason}
              />
              <ChatDetailSettingsButton
                isAutomatedChat={isAbleToChangePhoto}
                title="Change photo"
                onClick={() => setOpenChangeAvatarDialog(true)}
                disabledReason={changePhotoDisabledReason}
              />
              <Button
                size="small"
                style={{ width: '100%' }}
                onClick={() => setOpenLimitMessageSendersDialog(true)}
                color="primary"
                variant="outlined"
              >
                Limit message senders
              </Button>
            </Box>
          )}
        />
      )}

      <Divider />

      <Box display="flex" flexDirection="column" gridGap={16} marginTop={2}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gridGap={12}
          paddingRight={'1px'}
        >
          <Typography style={{ color: colors.textLight, fontSize: 13, fontWeight: 500 }}>
            Chat members
          </Typography>

          {chat.type === 'group' && (
            <AppIconButton
              variant="paper"
              disableFocusRipple
              disableRipple
              disableTouchRipple
              onClick={openAddMembersDialog}
            >
              <AiOutlinePlus size={21} color={theme.palette.text.secondary} />
            </AppIconButton>
          )}
        </Box>

        <Box display="flex" flexDirection="column" gridGap={16}>
          {chat.profiles?.map(profile => {
            return (
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box key={profile.uid} display="flex" alignItems="center" gridGap={12}>
                  <ChatAvatar avatarUrl={profile.avatarUrl} name={profile.name} size={35} borderWidth={1} />
                  <Typography style={{ color: colors.textLight, fontSize: 13, fontWeight: 500 }}>
                    {profile.name}
                  </Typography>
                </Box>

                {userId !== profile.uid && (
                  <GridActions variant="paper" close={false}>
                    <MenuList>
                      <MenuItem
                        onClick={() => {
                          setSelectedUser(profile);
                          setOpenRemoveUserDialog(true);
                        }}
                      >
                        <Box display="flex" alignItems="center" gridGap={8}>
                          <HiOutlineTrash size={14} />
                          Remove from chat
                        </Box>
                      </MenuItem>
                      <MenuItem onClick={() => handleGoToChat(profile)}>
                        <Box display="flex" alignItems="center" gridGap={8}>
                          {creatingChat ? <CircularProgress size={14} /> : <FiMessageCircle size={14} />}
                          Message
                        </Box>
                      </MenuItem>
                    </MenuList>
                  </GridActions>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <ChatChangeNameDialog
        open={openChangeNameDialog}
        handleClose={() => setOpenChangeNameDialog(false)}
        loading={loadingChatUpdate}
        confirmFunction={async () => {
          await updateChatName(chatName);
          setOpenChangeNameDialog(false);
        }}
        chatName={chatName}
        setChatName={setChatName}
      />

      <ChatChangeAvatarDialog
        open={openChangeAvatarDialog}
        handleClose={() => setOpenChangeAvatarDialog(false)}
        loading={loadingChatUpdate}
        confirmFunction={async () => {
          await updateChatAvatar(chatAvatar);
          setOpenChangeAvatarDialog(false);
        }}
        avatar={chatAvatar}
        setAvatar={setChatAvatar}
      />

      <ChatLimitMessageSendersDialog
        open={openLimitMessageSendersDialog}
        handleClose={() => setOpenLimitMessageSendersDialog(false)}
        loading={loadingChatUpdate}
        confirmFunction={async () => {
          await updateChatReadOnlyStatus(readOnly);
          setOpenLimitMessageSendersDialog(false);
        }}
        readOnly={readOnly}
        setReadOnly={setReadOnly}
      />

      <ConfirmDialog
        open={openRemoveUserDialog}
        handleClose={() => setOpenRemoveUserDialog(false)}
        handleConfirm={() => handleRemoveUser(selectedUser?.uid || '')}
        title="Remove user from chat"
        loading={loadingRemoveUser}
        confirmText="Remove"
        cancelText="Cancel"
      />
    </Box>
  );
};

const useStyles = makeStyles(theme => ({
  root: {
    padding: '16px 24px',
  },
  headerBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 16,
  },
  textBox: {
    marginLeft: 12,
    width: '100%',
    paddingRight: 12,
    overflow: 'hidden',
  },
  name: {
    fontWeight: 600,
    fontSize: 15,
    color: theme.palette.type === 'dark' ? monochrome.light : monochrome.dark,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  members: {
    fontSize: 12,
    color: theme.palette.type === 'dark' ? monochrome.mediumlight : monochrome.mediumdark,
    fontWeight: 500,
  },
  membersIcon: {
    fontSize: 16,
    marginRight: 4,
  },
  accordion: {
    padding: 0,
  },
}));
